import {
  getFilingDueDate,
  updateComplianceItem,
  useBusinessEntity,
  useSupportedFilings,
} from '@commenda/api';
import { createComplianceItem } from '@commenda/api/compliance/createComplianceItem';
import { CountryCode, FullscreenModal } from '@commenda/kakashi';
import mixpanel, {
  MIXPANEL_SOURCE,
  MIXPANEL_SURFACE_AREA,
} from '@commenda/mixpanel';
import { TComplianceItem } from '@commenda/types/src/compliance-calendar/compliances/types';
import {
  ObligationRulesCategory,
  ObligationRulesFrequency,
} from '@prisma/client';
import { useMemo, useState } from 'react';
import {
  NewIncorporationLayout,
  type ProgressStepItem,
} from '../../../form-builder/components/new-incorp-layout';
import { useNotification } from '../../../hooks/useNotification';
import { CreateFilingSheet } from './CreateFilingSheet';
import { FilingDetailsStep } from './steps/FilingDetailsStep';
import { FrequencyDueDateStep } from './steps/FrequencyDueDateStep';

export type FilingFormData = {
  selectedEntityId?: number;
  // Set when a filing from content is chosen; undefined for a manually created (custom) filing.
  complianceType?: string;
  // True when the filing was defined via the "Create new filing" sheet rather than picked from content.
  isCustom?: boolean;
  requiresManualDueDate?: boolean;
  filingName: string;
  category: ObligationRulesCategory | '';
  frequency: ObligationRulesFrequency;
  firstDueDate: string;
};

export enum FilingModalStepType {
  FILING_DETAILS = 'FILING_DETAILS',
  FREQUENCY_DUE_DATE = 'FREQUENCY_DUE_DATE',
}

interface AddCustomFilingModalProps {
  isOpen: boolean;
  onClose: () => void;
  businessEntityId?: number;
  companyId?: number;
  refreshCompliances: () => void;
  editingFiling?: TComplianceItem;
  loggedInUserId: string;
  isFromCalendarView?: boolean;
}

const getInitialFormData = (businessEntityId?: number): FilingFormData => ({
  selectedEntityId: businessEntityId,
  complianceType: undefined,
  isCustom: false,
  filingName: '',
  category: '' as ObligationRulesCategory,
  frequency: ObligationRulesFrequency.ANNUAL,
  firstDueDate: '',
});

// Full-page "Add filing" form. Opened from the Filings list; renders the shared new-incorp form shell
// (header + stepper sidebar + form + actions). The manual "Create new filing" path uses a bottom sheet.
export const AddCustomFilingModal = ({
  isOpen,
  onClose,
  companyId,
  businessEntityId,
  refreshCompliances,
  editingFiling,
  loggedInUserId,
  isFromCalendarView = false,
}: AddCustomFilingModalProps) => {
  const isEditMode = !!editingFiling;
  const needsEntitySelection =
    isFromCalendarView && !businessEntityId && !isEditMode;

  const [formData, setFormData] = useState<FilingFormData>(() =>
    getInitialFormData(businessEntityId)
  );
  const [currentStep, setCurrentStep] = useState<FilingModalStepType>(
    FilingModalStepType.FILING_DETAILS
  );
  const [completedSteps, setCompletedSteps] = useState<
    Set<FilingModalStepType>
  >(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFetchingDueDate, setIsFetchingDueDate] = useState(false);
  const [showCreateSheet, setShowCreateSheet] = useState(false);

  const { setNotification } = useNotification();

  const effectiveEntityId = formData.selectedEntityId || businessEntityId;
  const { data: businessEntity } = useBusinessEntity(effectiveEntityId || 0);
  const { data: supportedFilings } = useSupportedFilings(effectiveEntityId);
  const entityName = businessEntity?.generalCorporationInfo?.legalName?.trim();
  const entityCountry =
    businessEntity?.generalCorporationInfo?.incorporationCountry;
  const incorporationDate =
    businessEntity?.generalCorporationInfo?.formationDate || undefined;
  const isEntityLocked = !needsEntitySelection || !!businessEntityId;

  const steps = [
    { id: FilingModalStepType.FILING_DETAILS, text: 'Select filing' },
    {
      id: FilingModalStepType.FREQUENCY_DUE_DATE,
      text: 'Frequency & due date',
    },
  ];

  const currentStepIndex = useMemo(
    () => steps.findIndex((s) => s.id === currentStep),
    [currentStep]
  );
  const isLastStep = currentStepIndex === steps.length - 1;

  const canNavigateToStep = (step: FilingModalStepType): boolean => {
    const stepIndex = steps.findIndex((s) => s.id === step);
    return stepIndex <= currentStepIndex;
  };

  const handleStepClick = (step: FilingModalStepType) => {
    if (canNavigateToStep(step)) setCurrentStep(step);
  };

  const canContinue = (): boolean => {
    switch (currentStep) {
      case FilingModalStepType.FILING_DETAILS:
        return (
          (!needsEntitySelection || !!formData.selectedEntityId) &&
          (!!formData.complianceType ||
            (!!formData.isCustom &&
              !!formData.filingName &&
              !!formData.category))
        );
      case FilingModalStepType.FREQUENCY_DUE_DATE:
        return !!(formData.frequency && formData.firstDueDate);
      default:
        return false;
    }
  };

  const handleNext = async () => {
    if (isLastStep || !canContinue()) return;

    // Schedulable content filing: fetch the computed due date before the (disabled) review step.
    // Custom filings carry the sheet's date; manual-date filings (as-needed / not auto-generated)
    // have no derivable schedule, so the user picks the date on the review step instead.
    if (
      currentStep === FilingModalStepType.FILING_DETAILS &&
      !formData.isCustom &&
      !formData.requiresManualDueDate &&
      formData.complianceType &&
      effectiveEntityId
    ) {
      setIsFetchingDueDate(true);
      try {
        const { firstDueDate } = await getFilingDueDate(
          effectiveEntityId,
          formData.complianceType
        );
        setFormData((prev) => ({ ...prev, firstDueDate }));
      } catch (error) {
        setNotification({
          show: true,
          title: 'Error',
          message:
            'Could not load the due date for this filing. Please try again.',
          valid: false,
        });
        return;
      } finally {
        setIsFetchingDueDate(false);
      }
    }

    setCompletedSteps((prev) => new Set([...Array.from(prev), currentStep]));
    setCurrentStep(steps[currentStepIndex + 1].id);
  };

  const handleBack = () => {
    if (currentStepIndex > 0) setCurrentStep(steps[currentStepIndex - 1].id);
  };

  const handleStepDataUpdate = (stepData: Partial<FilingFormData>) => {
    setFormData((prev) => ({ ...prev, ...stepData }));
  };

  // The "Create new filing" sheet only defines the filing; we prefill the steps (disabled) and
  // the user reviews and clicks "Add filing" to actually persist it.
  const handleCustomFilingDefined = (filing: {
    filingName: string;
    category: ObligationRulesCategory;
    frequency: ObligationRulesFrequency;
    firstDueDate: string;
  }) => {
    setFormData((prev) => ({
      ...prev,
      complianceType: undefined,
      isCustom: true,
      filingName: filing.filingName,
      category: filing.category,
      frequency: filing.frequency,
      firstDueDate: filing.firstDueDate,
    }));
    setShowCreateSheet(false);
  };

  const handleClose = () => {
    setCurrentStep(FilingModalStepType.FILING_DETAILS);
    setFormData(getInitialFormData(businessEntityId));
    setCompletedSteps(new Set());
    setShowCreateSheet(false);
    onClose();
  };

  // Content filing (selected from the dropdown) → persist by complianceType only; the backend derives
  // title/category/frequency and the due date from content. Custom filings send their authored fields.
  const handleSubmit = async () => {
    if (
      !effectiveEntityId ||
      (!formData.complianceType && !formData.isCustom)
    ) {
      setNotification({
        show: true,
        title: 'Error',
        message: 'Select a filing before adding.',
        valid: false,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const res = formData.isCustom
        ? await createComplianceItem({
            corporationId: effectiveEntityId,
            dueDate: formData.firstDueDate,
            fiscalYear: new Date(formData.firstDueDate).getFullYear(),
            isCustom: true,
            customFields: {
              explicitTitle: formData.filingName.trim(),
              customFrequency: formData.frequency,
              customCategory: formData.category as ObligationRulesCategory,
            },
          })
        : await createComplianceItem({
            corporationId: effectiveEntityId,
            complianceType: formData.complianceType,
            ...(formData.requiresManualDueDate
              ? { dueDate: formData.firstDueDate }
              : {}),
          });

      if (!res.ok) {
        setNotification({
          show: true,
          title: 'Error',
          message: 'Failed to add filing. Please try again.',
          valid: false,
        });
        return;
      }

      const { compliance } = (await res.json()) as {
        compliance: { id: string };
      };
      if (compliance?.id) {
        try {
          await updateComplianceItem(compliance.id, {
            assigneeIdsToAdd: [loggedInUserId],
          });
        } catch (error) {
          console.error('Failed to add default assignee:', error);
        }
      }

      mixpanel.track('CALENDAR_CUSTOM_FILING_ADDED', {
        source: MIXPANEL_SOURCE.CALENDAR,
        surface_area: MIXPANEL_SURFACE_AREA.COMPLIANCE_CALENDAR,
        company_id: businessEntity?.generalCorporationInfo?.companyId,
        is_edit: false,
      });

      setNotification({
        show: true,
        title: 'Success',
        message: 'Filing added successfully!',
        valid: true,
      });
      handleClose();
      refreshCompliances();
    } catch (error) {
      setNotification({
        show: true,
        title: 'Error',
        message: 'Failed to add filing. Please try again.',
        valid: false,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  // Editing an existing custom filing happens entirely in the bottom sheet.
  if (isEditMode) {
    return (
      <CreateFilingSheet
        isOpen={isOpen}
        onClose={handleClose}
        corporationId={effectiveEntityId}
        companyId={companyId}
        loggedInUserId={loggedInUserId}
        editingFiling={editingFiling}
        refreshCompliances={refreshCompliances}
        incorporationDate={incorporationDate}
      />
    );
  }

  const getStepContent = () => {
    switch (currentStep) {
      case FilingModalStepType.FILING_DETAILS:
        return (
          <FilingDetailsStep
            formData={formData}
            onDataUpdate={handleStepDataUpdate}
            needsEntitySelection={needsEntitySelection}
            companyId={companyId}
            supportedFilings={supportedFilings ?? []}
            onCreateNew={() => setShowCreateSheet(true)}
          />
        );
      case FilingModalStepType.FREQUENCY_DUE_DATE:
        return (
          <FrequencyDueDateStep
            formData={formData}
            onDataUpdate={handleStepDataUpdate}
            incorporationDate={incorporationDate}
            disabled
            dueDateEditable={
              !formData.isCustom && !!formData.requiresManualDueDate
            }
          />
        );
      default:
        return null;
    }
  };

  const progressSteps: ProgressStepItem[] = steps.map((step, index) => ({
    id: step.id,
    text: step.text,
    isCompleted: completedSteps.has(step.id),
    disabled: index > currentStepIndex,
  }));

  return (
    <>
      <FullscreenModal isOpen={isOpen} onClose={handleClose} enableEscapeKey>
        <NewIncorporationLayout
          steps={progressSteps}
          activeStepIndex={currentStepIndex}
          onStepClick={(index) => {
            const target = steps[index];
            if (target) handleStepClick(target.id);
          }}
          title="Add filing"
          header={{
            entity:
              entityName && isEntityLocked && entityCountry
                ? {
                    name: entityName,
                    countryCode: entityCountry as CountryCode,
                  }
                : undefined,
            onClose: handleClose,
          }}
          formActions={{
            onBack: currentStepIndex > 0 ? handleBack : undefined,
            hideBack: currentStepIndex === 0,
            onNext: isLastStep
              ? () => {
                  handleSubmit();
                }
              : () => {
                  handleNext();
                },
            nextLabel: isLastStep ? 'Add filing' : 'Continue',
            disableNext: isLastStep
              ? isSubmitting || !canContinue()
              : isFetchingDueDate || !canContinue(),
            isLoading: isSubmitting || isFetchingDueDate,
          }}
        >
          <div className="mt-4">{getStepContent()}</div>
        </NewIncorporationLayout>
      </FullscreenModal>

      {/* Manual "Create new filing" path — the Sheet (z-50) layers above the form (z-40). */}
      <CreateFilingSheet
        isOpen={showCreateSheet}
        onClose={() => setShowCreateSheet(false)}
        onFilingDefined={handleCustomFilingDefined}
        corporationId={effectiveEntityId}
        companyId={companyId}
        loggedInUserId={loggedInUserId}
        refreshCompliances={refreshCompliances}
        incorporationDate={incorporationDate}
      />
    </>
  );
};
